import React, { useEffect, useState } from "react";
import { 
  Box, 
  Fade, 
  CircularProgress, 
  Alert, 
  AlertIcon, 
  Text, 
  Flex,
  Container,
  HStack,
  Button,
  Divider
} from "@chakra-ui/react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useClient } from "../hooks/useClients";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { ClientInfo } from "../components/clients/ClientInfo";

const ClientInfoPage: React.FC = () => {
  const params = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoaded, setIsLoaded] = useState(false);
  
  // Get name and email from URL search params
  const searchParams = new URLSearchParams(location.search);
  const clientName = searchParams.get('name') || '';
  const clientEmail = searchParams.get('email') || '';
  
  const clientId = params.id || "1";
  
  // Use the client hook
  const { client, loading, error } = useClient({ 
    clientId, 
    enabled: !!clientId 
  });

  useEffect(() => {
    setIsLoaded(true);
  }, [clientId, clientName, clientEmail]);
  
  const handleBack = () => {
    navigate('/clients');
  };

  const handleViewFullDetails = () => {
    navigate(`/client-details/${clientId}?name=${encodeURIComponent(clientName)}&email=${encodeURIComponent(clientEmail)}`);
  };

  if (loading) {
    return (
      <Flex justify="center" align="center" height="100vh">
        <CircularProgress isIndeterminate color="purple.500" />
        <Text ml={4}>Loading client information...</Text>
      </Flex>
    );
  }

  if (error) {
    return (
      <Flex justify="center" align="center" height="100vh" direction="column">
        <Alert status="error" variant="subtle" flexDirection="column" alignItems="center" justifyContent="center" textAlign="center" height="200px">
          <AlertIcon boxSize="40px" mr={0} />
          <Text fontSize="xl" mt={4} mb={2} fontWeight="bold">
            Error Loading Client Information
          </Text>
          <Text>{error}</Text>
        </Alert>
      </Flex>
    );
  }

  if (!client) {
    return (
      <Flex justify="center" align="center" height="100vh" direction="column">
        <Text fontSize="lg" color="gray.500">
          Client not found
        </Text>
      </Flex>
    );
  }
  
  return (
    <Fade in={isLoaded} transition={{ enter: { duration: 0.3 } }}>
      <Box height="100vh" bg="gray.50">
        {/* Header */}
        <Box bg="white" borderBottom="1px solid" borderColor="gray.200" py={4}>
          <Container maxW="container.xl">
            <HStack justify="space-between" align="center">
              <HStack spacing={4}>
                <Button
                  leftIcon={<ArrowBackIcon />}
                  variant="ghost"
                  onClick={handleBack}
                  size="sm"
                >
                  Back to Clients
                </Button>
                <Divider orientation="vertical" height="24px" />
                <Text fontSize="xl" fontWeight="bold" color="gray.800">
                  Client Information
                </Text>
              </HStack>
              <Button
                size="sm"
                colorScheme="purple"
                onClick={handleViewFullDetails}
              >
                View Full Details
              </Button>
            </HStack>
          </Container>
        </Box>

        {/* Main Content */}
        <Container maxW="container.sm" py={8}>
          <Box bg="white" borderRadius="lg" boxShadow="lg" border="1px solid" borderColor="gray.200" overflow="hidden">
            <ClientInfo
              name={client.name || clientName}
              status={client.status}
              email={client.email || clientEmail}
              phone={client.phone || 'N/A'}
            />
          </Box>
        </Container>
      </Box>
    </Fade>
  );
};

export default ClientInfoPage; 